import { ElementType, ReactNode, SyntheticEvent } from 'react';
import {
  Button as MUIButton,
  ButtonProps as MUIButtonProps,
  Theme,
} from '@mui/material';
import { SystemStyleObject } from '@mui/system';

import colors from '@atom/styles/colors';

// box shadow used for Button in MUI 0.4
const MUI_0_BUTTON_BOX_SHADOW =
  'rgb(0 0 0 / 12%) 0px 1px 6px, rgb(0 0 0 / 12%) 0px 1px 4px';

type AtomMUIProps = {
  component?: ElementType;
  color?:
    | 'inherit'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'error'
    | 'info'
    | 'warning';
  children?: ReactNode;
  variant?: 'text' | 'outlined' | 'contained';
  onClick?: (event: SyntheticEvent<any>) => void;
  disabled?: boolean;
  startIcon?: ReactNode;
  size?: 'medium' | 'large' | 'small';
  type?: 'button' | 'reset' | 'submit';
  sx?: SystemStyleObject<Theme>;
};

export type ButtonProps = AtomMUIProps;

export const Button = (props: ButtonProps) => {
  const { color = 'inherit', variant, sx: sxProp, ...data } = props;

  const MUIProps = data as MUIButtonProps;

  const getVariantColorStyles = (): SystemStyleObject<Theme> => {
    switch (true) {
      case variant === 'contained' && color === 'primary':
        return {
          boxShadow: MUI_0_BUTTON_BOX_SHADOW,
          '&:hover': {
            boxShadow: MUI_0_BUTTON_BOX_SHADOW,
          },
          color: colors.neutral.white,
          backgroundColor: colors.brand.blue,
        };
      case variant === 'text' && color === 'inherit':
        return { color: colors.neutral.black };
      case variant === 'text' && color === 'primary':
        return { color: colors.brand.blue };
      case variant === 'outlined' && color === 'inherit':
        return { border: `1px solid ${colors.neutral.silver}` };
      default:
        return {};
    }
  };

  const baseSx: SystemStyleObject<Theme> = {
    borderRadius: '2px',
    minWidth: '88px',
    padding: '6px 8px',
    whiteSpace: 'nowrap',
    ...getVariantColorStyles(),
  };

  return (
    <MUIButton
      {...MUIProps}
      color={color}
      variant={variant}
      sx={sxProp ? [baseSx, sxProp] : baseSx}
    />
  );
};

export default Button;
